import {
  autoUpdate,
  flip,
  FlipOptions,
  Middleware,
  offset,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';
import React from 'react';

type Props = {
  toolbarRef: React.RefObject<HTMLElement>;
  posRef?: React.RefObject<HTMLElement>;
  mainAxisOffset?: number;
  /**
   * Above the toolbar, or below it for a toolbar at the top. Centered on the icon, or with -end,
   * lined up with its right edge, for a toolbar at the right side of the view, beside it where
   * they don't fit below.
   * @default 'top'
   */
  placement?: SettingsPanelPlacement;
};

type SettingsPanelPlacement = 'top' | 'bottom' | 'bottom-end';

/**
 * Flips the settings by their full size, not as size has made them fit with a scroll bar, or they
 * would flip back to where they don't fit.
 */
const flipAtFullSize = (options: FlipOptions): Middleware => {
  const middleware = flip(options);
  return {
    ...middleware,
    fn: (state) => {
      const { floating } = state.rects;
      return middleware.fn({
        ...state,
        rects: {
          ...state.rects,
          floating: {
            ...floating,
            width: Math.max(floating.width, state.elements.floating.scrollWidth),
            height: Math.max(floating.height, state.elements.floating.scrollHeight),
          },
        },
      });
    },
  };
};

/**
 * Places the settings clear of the toolbar, see Props.placement, and makes them scroll where they
 * still don't fit.
 */
const getMiddleware = (
  placement: SettingsPanelPlacement,
  mainAxisOffset: number,
  toolbarRef: React.RefObject<HTMLElement>,
): Middleware[] => [
  // Clear of the toolbar, not only of the icon, on the side the settings are on
  offset(({ placement: side, elements }) => {
    const reference = elements.reference.getBoundingClientRect();
    const toolbar = toolbarRef.current!.getBoundingClientRect();
    if (side.startsWith('left')) {
      // From the top of the toolbar, for all of the view's height
      return {
        mainAxis: reference.left - toolbar.left + mainAxisOffset,
        crossAxis: toolbar.top - reference.top,
      };
    }
    return (
      (side.startsWith('top') ? reference.top - toolbar.top : toolbar.bottom - reference.bottom) +
      mainAxisOffset
    );
  }),
  flipAtFullSize({
    padding: 10,
    // Over the toolbar above them, they'd hide it
    fallbackPlacements: placement === 'bottom-end' ? ['left-start'] : undefined,
  }),
  // Scrolled where they still don't fit, as on a small screen. Before shift, which would otherwise
  // move them over what is above them, e.g. beside the toolbar from its top
  size({
    padding: 10,
    apply: ({ availableWidth, availableHeight, elements }) => {
      elements.floating.style.maxWidth = `${availableWidth}px`;
      elements.floating.style.maxHeight = `${availableHeight}px`;
    },
  }),
  shift({
    padding: 10,
  }),
];

const useSettingsPanel = ({ mainAxisOffset = 0, placement = 'top', ...props }: Props) => {
  const [open, setOpen] = React.useState(false);
  const floatingData = useFloating({
    placement,
    open,
    onOpenChange: (nextOpen, event, reason) => {
      setOpen(nextOpen);
    },
    whileElementsMounted: autoUpdate,
    middleware: getMiddleware(placement, mainAxisOffset, props.toolbarRef),
    strategy: 'fixed',
  });

  const click = useClick(floatingData.context, {});
  const dismiss = useDismiss(floatingData.context, {});
  const interactions = useInteractions([click, dismiss]);

  return React.useMemo(
    () => ({
      open,
      ...interactions,
      ...floatingData,
    }),
    [open, interactions, floatingData],
  );
};

export {
  getMiddleware as getSettingsPanelMiddleware,
  SettingsPanelPlacement,
  useSettingsPanel,
  Props as UseSettingsPanelProps,
};
